<script lang="tsx">
import {
  defineComponent, nextTick,
  ref,
  type PropType, type Ref,
} from 'vue'
import useClickOutside from '../../../../composables/use-click-outside'
import { type ComputedColumnInterface } from '../../DefaultTableInterfaces'
import { TextTableFilter } from '../../filters/Filters'
import HeaderSort from './DefaultTableHeaderSort.vue'

export default defineComponent({
  props: {
    column: {
      type: Object as PropType<ComputedColumnInterface>,
      required: true,
    },
    onSort: {
      type: Function as PropType<() => void>,
    },
    sortField: {
      type: String,
      required: true,
    },
    enableSorting: {
      type: Boolean,
    },
    sortOrder: {
      type: Boolean as PropType<boolean>,
    },
  },
  setup(props) {
    const isBodyActive: Ref<boolean> = ref(false)
    const filter: TextTableFilter = props.column.filter as TextTableFilter
    if (!(filter instanceof TextTableFilter)) throw new Error('Неверный тип фильтра передан в компонент')

    const root: Ref<HTMLDivElement | null> = ref(null)
    const input: Ref<HTMLInputElement | null> = ref(null)

    const toggleIsBodyActive = async (val: boolean) => {
      isBodyActive.value = val

      if (input.value && val === true) {
        await nextTick()
        input.value.focus()
      }
    }

    const setSearch = (val: string) => {
      if (!val) {
        filter.removeValue()
        input.value?.focus()
      } else {
        filter.addValue(val)
      }
    }

    const inputHandler = (e: Event) => {
      setSearch((e.target as HTMLInputElement).value)
    }

    useClickOutside(root, () => toggleIsBodyActive(false))

    return () => (

      <div ref={root} class="ark-ui-table-text-header">
        <div class={`ark-ui-table-text-header-head ${isBodyActive.value ? 'active' : ''}`} onClick={() => toggleIsBodyActive(!isBodyActive.value)}>
          <div class="ark-ui-table-text-header-text">{ props.column.name }</div>

          <div class="ark-ui-table-text-header-buttons">
            <div class="ark-ui-table-text-header-search-box">
              <svg class="ark-ui-table-text-header-search" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* eslint-disable-next-line max-len */}
                <path fill={`#${isBodyActive.value ? '585B69' : filter.value ? '30C1CF' : 'A3A7B9'}`} fill-rule="evenodd" clip-rule="evenodd" d="M10.8744 11.5815C9.82908 12.4664 8.47685 13 7 13C3.68629 13 1 10.3137 1 7C1 3.68629 3.68629 1 7 1C10.3137 1 13 3.68629 13 7C13 8.47685 12.4664 9.82908 11.5815 10.8744L13.8536 13.1464C14.0488 13.3417 14.0488 13.6583 13.8536 13.8536C13.6583 14.0488 13.3417 14.0488 13.1464 13.8536L10.8744 11.5815ZM12 7C12 9.76142 9.76142 12 7 12C4.23858 12 2 9.76142 2 7C2 4.23858 4.23858 2 7 2C9.76142 2 12 4.23858 12 7Z" />
              </svg>
            </div>

            { props.enableSorting && props.onSort && (
              <HeaderSort column={props.column} onSort={props.onSort} sortField={props.sortField} sortOrder={props.sortOrder} />
            ) }
          </div>
        </div>

        <div class={`ark-ui-table-text-header-body ${isBodyActive.value ? 'active' : ''}`}>

          <input ref={input} onInput={inputHandler} value={filter.value} class="ark-ui-table-text-header-input" type="text" placeholder="Поиск" />
          <button onClick={() => setSearch('')} class="ark-ui-table-text-header-reset">
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* eslint-disable-next-line max-len */}
              <path d="M0.146721 0.146447C0.341983 -0.0488155 0.658566 -0.0488155 0.853828 0.146447L4.00027 3.29289L7.14672 0.146447C7.34198 -0.0488155 7.65857 -0.0488155 7.85383 0.146447C8.04909 0.341709 8.04909 0.658291 7.85383 0.853553L4.70738 4L7.85383 7.14645C8.04909 7.34171 8.04909 7.65829 7.85383 7.85355C7.65857 8.04882 7.34198 8.04882 7.14672 7.85355L4.00027 4.70711L0.853828 7.85355C0.658566 8.04882 0.341983 8.04882 0.146721 7.85355C-0.0485409 7.65829 -0.0485409 7.34171 0.146721 7.14645L3.29317 4L0.146721 0.853553C-0.0485409 0.658291 -0.0485409 0.341709 0.146721 0.146447Z" fill="#A3A7B9" />
            </svg>
          </button>

        </div>
      </div>

    )
  },
})
</script>

<style lang="scss" scoped>
.ark-ui-table-text-header {
  position: relative;

  &-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    min-height: 24px;
    padding: 12px 16px;

    // cursor: pointer;

    &.active,
    &:hover {
      & path {
        fill: #585B69;
      }
    }
  }

  &-text {
    align-self: center;
    min-width: 40px;
    margin-right: 13px;

    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    color: #585B69;
    font-size: 14px;
    font-weight: 500;
    line-height: 16px;
  }

  &-buttons {
    display: flex;
    align-items: center;
  }

  &-search,
  &-reset {
    padding: 0;
    border: none;
    cursor: pointer;
    background: transparent;
  }

  &-search {
    &-box {
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 2px;
      width: 24px;
      height: 24px;
      &:hover {
        background-color: #D3D5E5;
      }
    }
  }

  &-body {
    width: calc(100% + 28px);
    display: none;
    align-items: center;
    padding: 12px 16px;
    position: absolute;
    box-sizing: border-box;

    background-color: #fff;
    box-shadow: 0 8px 16px rgba(75, 77, 89, 0.08);

    &.active {
      display: flex;
    }
  }

  &-input {
    width: 100%;
    border: none;
    outline: none;
    background: none;

    font-size: 14px;
    color: #585B69;
    font-weight: 500;
    line-height: 16px;

    &::placeholder {
      color: #A3A7B9;
      font-weight: 400;
    }
  }

  &-reset {
    width: 16px;
    height: 16px;
    margin-left: 8px;
  }
}
</style>
